/**
 * Invoice Parser — extracts text from uploaded invoices (image/PDF),
 * detects supplier, date, amounts, and line items.
 */

// ── PDF text extraction ──────────────────────────────────────────────
export async function extractTextFromPDF(file) {
  const { configurePdfWorker, pdfjsLib } = await import('./pdfWorker')
  configurePdfWorker()

  const arrayBuffer = await file.arrayBuffer()
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
  const pages = []

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i)
    const content = await page.getTextContent()
    const text = content.items.map(item => item.str).join(' ')
    pages.push(text)
  }

  return pages.join('\n')
}

// ── Image OCR via Tesseract.js ───────────────────────────────────────
export async function extractTextFromImage(file, onProgress) {
  const Tesseract = await import('tesseract.js')
  const worker = await Tesseract.createWorker('eng', 1, {
    logger: m => {
      if (onProgress && m.status === 'recognizing text') {
        onProgress(Math.round(m.progress * 100))
      }
    }
  })

  const result = await worker.recognize(file)
  await worker.terminate()
  return result.data.text
}

// ── Extract text from any supported file ─────────────────────────────
export async function extractTextFromFile(file, onProgress) {
  const type = file.type
  const ext = file.name?.split('.').pop()?.toLowerCase()

  if (type === 'application/pdf' || ext === 'pdf') {
    return await extractTextFromPDF(file)
  }

  if (type?.startsWith('image/') || ['png', 'jpg', 'jpeg', 'webp', 'bmp'].includes(ext)) {
    return await extractTextFromImage(file, onProgress)
  }

  throw new Error(`Unsupported file type: ${type || ext}`)
}

// ── Fuzzy match supplier name ────────────────────────────────────────
function normalise(str) {
  return (str || '').toLowerCase().replace(/[^a-z0-9]/g, '')
}

function similarity(a, b) {
  const na = normalise(a)
  const nb = normalise(b)
  if (!na || !nb) return 0
  // Check containment first
  if (na.includes(nb) || nb.includes(na)) return 0.9
  // Levenshtein-based similarity
  const len = Math.max(na.length, nb.length)
  const dist = levenshtein(na, nb)
  return 1 - dist / len
}

function levenshtein(a, b) {
  const m = a.length
  const n = b.length
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0))
  for (let i = 0; i <= m; i++) dp[i][0] = i
  for (let j = 0; j <= n; j++) dp[0][j] = j
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1])
    }
  }
  return dp[m][n]
}

export function matchSupplier(extractedText, suppliers) {
  if (!extractedText || !suppliers?.length) return null

  const text = extractedText.toLowerCase()
  let bestMatch = null
  let bestScore = 0

  for (const supplier of suppliers) {
    // Check supplier name
    const nameScore = similarity(text, supplier.name)

    // Also check if supplier name appears as a substring in the text
    const supplierNorm = normalise(supplier.name)
    const directMatch = text.replace(/[^a-z0-9]/g, '').includes(supplierNorm) ? 0.95 : 0

    // Check contact person name too
    const contactScore = supplier.contactPerson ? similarity(text, supplier.contactPerson) * 0.7 : 0

    // Check phone number (exact match in text)
    const phoneClean = (supplier.phone || '').replace(/\D/g, '')
    const phoneMatch = phoneClean.length >= 7 && text.replace(/\D/g, '').includes(phoneClean) ? 0.85 : 0

    // Check email
    const emailMatch = supplier.email && text.includes(supplier.email.toLowerCase()) ? 0.9 : 0

    const score = Math.max(nameScore, directMatch, contactScore, phoneMatch, emailMatch)

    if (score > bestScore) {
      bestScore = score
      bestMatch = { supplier, score, matchType: getMatchType(score, directMatch, phoneMatch, emailMatch) }
    }
  }

  // Only return if confidence is reasonable
  return bestScore >= 0.5 ? bestMatch : null
}

function getMatchType(score, directMatch, phoneMatch, emailMatch) {
  if (directMatch >= 0.95) return 'name'
  if (emailMatch >= 0.9) return 'email'
  if (phoneMatch >= 0.85) return 'phone'
  if (score >= 0.7) return 'fuzzy'
  return 'partial'
}

// ── Extract date from invoice text ───────────────────────────────────
export function extractDate(text) {
  if (!text) return null

  // Common date patterns
  const patterns = [
    // DD/MM/YYYY or DD-MM-YYYY
    /(\d{1,2})[\/\-](\d{1,2})[\/\-](20\d{2})/,
    // YYYY/MM/DD or YYYY-MM-DD
    /(20\d{2})[\/\-](\d{1,2})[\/\-](\d{1,2})/,
    // DD Month YYYY
    /(\d{1,2})\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+(20\d{2})/i,
    // Month DD, YYYY
    /(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+(\d{1,2}),?\s+(20\d{2})/i,
  ]

  const monthMap = { jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
    jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12' }

  for (const pattern of patterns) {
    const match = text.match(pattern)
    if (match) {
      if (/20\d{2}/.test(match[1])) {
        // YYYY-MM-DD format
        return `${match[1]}-${match[2].padStart(2, '0')}-${match[3].padStart(2, '0')}`
      }
      if (/[a-z]/i.test(match[2])) {
        // DD Month YYYY
        const mm = monthMap[match[2].toLowerCase().slice(0, 3)]
        return `${match[3]}-${mm}-${match[1].padStart(2, '0')}`
      }
      if (/[a-z]/i.test(match[1])) {
        // Month DD YYYY
        const mm = monthMap[match[1].toLowerCase().slice(0, 3)]
        return `${match[3]}-${mm}-${match[2].padStart(2, '0')}`
      }
      // DD/MM/YYYY (SA format)
      return `${match[3]}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`
    }
  }

  return null
}

// ── Extract total amount from invoice text ───────────────────────────
export function extractTotal(text) {
  if (!text) return null

  // Look for "total" followed by an amount
  const totalPatterns = [
    /total\s*:?\s*R?\s*([\d,]+\.?\d*)/i,
    /amount\s*due\s*:?\s*R?\s*([\d,]+\.?\d*)/i,
    /grand\s*total\s*:?\s*R?\s*([\d,]+\.?\d*)/i,
    /balance\s*due\s*:?\s*R?\s*([\d,]+\.?\d*)/i,
    /nett?\s*:?\s*R?\s*([\d,]+\.?\d*)/i,
  ]

  for (const pattern of totalPatterns) {
    const match = text.match(pattern)
    if (match) {
      return parseFloat(match[1].replace(/,/g, ''))
    }
  }

  // Fallback: find the largest R amount in the text
  const amounts = []
  const amountRegex = /R\s*([\d,]+\.?\d*)/g
  let m
  while ((m = amountRegex.exec(text)) !== null) {
    const val = parseFloat(m[1].replace(/,/g, ''))
    if (val > 0) amounts.push(val)
  }

  return amounts.length > 0 ? Math.max(...amounts) : null
}

// ── Extract line items from invoice text ─────────────────────────────
export function extractLineItems(text) {
  if (!text) return []

  const items = []
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean)

  for (const line of lines) {
    // Pattern: name/description ... qty ... price ... total
    // Look for lines with at least one number that looks like a price (has decimals or R prefix)
    const priceMatch = line.match(/R?\s*(\d+[.,]\d{2})/g)
    if (!priceMatch || priceMatch.length === 0) continue

    // Try to extract: description, qty, unit price, line total
    const fullPattern = /^(.+?)\s+(\d+(?:\.\d+)?)\s+R?\s*(\d+[.,]\d{2})\s+R?\s*(\d+[.,]\d{2})\s*$/
    const match = line.match(fullPattern)

    if (match) {
      const name = match[1].trim()
      const qty = parseFloat(match[2])
      const unitPrice = parseFloat(match[3].replace(',', '.'))
      const total = parseFloat(match[4].replace(',', '.'))

      // Skip header-like lines
      if (/^(description|item|product|qty|quantity|price|total|amount|unit)/i.test(name)) continue
      // Skip lines that are just numbers
      if (/^\d+$/.test(name)) continue

      items.push({ name, qty: String(qty), unitPrice: String(unitPrice), inventoryItemId: '' })
      continue
    }

    // Simpler pattern: name + amount
    const simplePattern = /^(.{3,}?)\s+R?\s*(\d+[.,]\d{2})\s*$/
    const simpleMatch = line.match(simplePattern)
    if (simpleMatch) {
      const name = simpleMatch[1].trim()
      const price = parseFloat(simpleMatch[2].replace(',', '.'))

      if (/^(description|item|product|qty|quantity|price|total|amount|unit|sub|vat|tax|discount)/i.test(name)) continue
      if (/^\d+$/.test(name)) continue

      items.push({ name, qty: '1', unitPrice: String(price), inventoryItemId: '' })
    }
  }

  return items
}

// ── Extract potential supplier name from invoice header ──────────────
export function extractSupplierName(text) {
  if (!text) return null

  const lines = text.split('\n').map(l => l.trim()).filter(Boolean)

  // The supplier name is usually one of the first non-trivial lines
  // Skip common header words
  const skipWords = ['invoice', 'tax invoice', 'credit note', 'delivery note', 'quotation',
    'quote', 'statement', 'receipt', 'date', 'page', 'ref', 'tel', 'fax', 'email',
    'vat', 'reg', 'registration', 'number', 'no.', 'address', 'to:', 'from:', 'bill to',
    'ship to', 'customer']

  for (let i = 0; i < Math.min(lines.length, 8); i++) {
    const line = lines[i]
    // Skip short lines, pure numbers, dates
    if (line.length < 3) continue
    if (/^\d+[\/-]\d+/.test(line)) continue
    if (/^(tel|fax|email|phone|cell|www|http)/i.test(line)) continue

    const lower = line.toLowerCase()
    const isSkip = skipWords.some(w => lower === w || lower.startsWith(w + ' ') || lower.startsWith(w + ':'))
    if (isSkip) continue

    // Likely a business name — usually has letters, possibly (Pty) Ltd, CC, etc.
    if (/[a-zA-Z]{2,}/.test(line) && line.length >= 3 && line.length <= 80) {
      return line
    }
  }

  return null
}

// ── Full invoice parse pipeline ──────────────────────────────────────
export async function parseInvoice(file, suppliers, onProgress) {
  const text = await extractTextFromFile(file, onProgress)

  const supplierMatch = matchSupplier(text, suppliers)
  const extractedName = extractSupplierName(text)
  const date = extractDate(text)
  const total = extractTotal(text)
  const lineItems = extractLineItems(text)

  return {
    rawText: text,
    supplierMatch,          // { supplier, score, matchType } or null
    extractedSupplierName: extractedName,
    date,
    total,
    lineItems,
    fileType: file.type?.startsWith('image/') ? 'image' : 'pdf',
    fileName: file.name,
  }
}
